// Client-side usage telemetry helper. Writes page-view and session-start
// events to Firestore /usageLogs under the current user's uid.
//
// Both helpers wait for Firebase Auth's own verdict before reading
// `auth.currentUser`: the app shell can render from the persisted session a
// few seconds before Auth has confirmed it (lib/persistedSession.ts), and
// during that window `currentUser` is still null.

import { collection, addDoc } from 'firebase/firestore';
import { db, auth } from './firebase';

export interface UsageEvent {
  userUid: string;
  userEmail: string;
  action: 'session_start' | 'page_view';
  page: string;
  createdAt: string;
}

/** Log when the PWA is loaded / active */
export async function logSessionStart() {
  // Capture the launch page before waiting — the user may navigate meanwhile.
  const page = window.location.pathname;
  await auth.authStateReady();
  const currentUser = auth.currentUser;
  if (!currentUser) return;

  try {
    const payload: UsageEvent = {
      userUid: currentUser.uid,
      userEmail: currentUser.email || 'anonymous@onlogic.com',
      action: 'session_start',
      page,
      createdAt: new Date().toISOString(),
    };

    await addDoc(collection(db, 'usageLogs'), payload);
  } catch (err) {
    // Fail silently so telemetry doesn't disrupt user experience
    // eslint-disable-next-line no-console
    console.warn('Failed to log telemetry session_start:', err);
  }
}

/** Log when the user navigates to a new page/route */
export async function logPageView(pageName: string) {
  await auth.authStateReady();
  const currentUser = auth.currentUser;
  if (!currentUser) return;

  try {
    const payload: UsageEvent = {
      userUid: currentUser.uid,
      userEmail: currentUser.email || 'anonymous@onlogic.com',
      action: 'page_view',
      page: pageName,
      createdAt: new Date().toISOString(),
    };

    await addDoc(collection(db, 'usageLogs'), payload);
  } catch (err) {
    // Fail silently
    // eslint-disable-next-line no-console
    console.warn(`Failed to log telemetry page_view for ${pageName}:`, err);
  }
}
